import { useEffect, useState } from 'react';

/**
 * Media Query Hook for Responsive Design
 *
 * This hook provides a React-friendly interface for responsive design using the same
 * breakpoints defined in src/styles/responsive.scss
 *
 * Breakpoints:
 * - small: 360px
 * - medium: 600px
 * - large: 1024px
 * - xlarge: 1270px
 * - xxlarge: 1440px
 *
 * @example Basic usage
 * ```tsx
 * import { useMediaQuery, useResponsive, useMediaQueryUp } from 'utils/hooks/useMediaQuery';
 *
 * const MyComponent = () => {
 *   // Basic media query
 *   const isMobile = useMediaQuery('(max-width: 768px)');
 *
 *   // Using predefined breakpoints
 *   const isDesktop = useMediaQueryUp('large'); // >= 1024px
 *
 *   // Using the convenience hook
 *   const { isMobile, isTablet, isDesktop } = useResponsive();
 *
 *   // Responsive values
 *   const columns = useResponsiveValue({
 *     small: 1,
 *     medium: 2,
 *     large: 3,
 *     xlarge: 4
 *   }, 1);
 *
 *   return (
 *     <div style={{ columns }}>
 *       {isMobile && <MobileComponent />}
 *       {isDesktop && <DesktopComponent />}
 *     </div>
 *   );
 * };
 * ```
 */

// Breakpoint values matching responsive.scss
export const BREAKPOINTS = {
  small: 360,
  medium: 600,
  large: 1024,
  xlarge: 1270,
  xxlarge: 1440
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;

/**
 * Custom hook for media queries with predefined breakpoints
 * @param query - Media query string or breakpoint key
 * @returns boolean indicating if the media query matches
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(false);

  useEffect(() => {
    const media = window.matchMedia(query);

    // Set initial value
    setMatches(media.matches);

    // Create listener function
    const listener = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Add listener
    media.addEventListener('change', listener);

    // Cleanup
    return () => {
      media.removeEventListener('change', listener);
    };
  }, [query]);

  return matches;
};

/**
 * Hook for checking if screen is at least a specific breakpoint
 * @param breakpoint - Breakpoint key
 * @returns boolean indicating if screen is at least the specified breakpoint
 */
export const useMediaQueryUp = (breakpoint: BreakpointKey): boolean => {
  const query = `(min-width: ${BREAKPOINTS[breakpoint]}px)`;
  return useMediaQuery(query);
};

/**
 * Hook for checking if screen is below a specific breakpoint
 * @param breakpoint - Breakpoint key
 * @returns boolean indicating if screen is below the specified breakpoint
 */
export const useMediaQueryDown = (breakpoint: BreakpointKey): boolean => {
  const query = `(max-width: ${BREAKPOINTS[breakpoint] - 1}px)`;
  return useMediaQuery(query);
};

/**
 * Hook for checking if screen is only within a specific breakpoint range
 * @param breakpoint - Breakpoint key
 * @returns boolean indicating if screen is only within the specified breakpoint range
 */
export const useMediaQueryOnly = (breakpoint: BreakpointKey): boolean => {
  const breakpointKeys = Object.keys(BREAKPOINTS) as BreakpointKey[];
  const currentIndex = breakpointKeys.indexOf(breakpoint);

  const minWidth = BREAKPOINTS[breakpoint];
  const nextBreakpoint = breakpointKeys[currentIndex + 1];

  let query: string;
  if (nextBreakpoint) {
    const maxWidth = BREAKPOINTS[nextBreakpoint] - 1;
    query = `(min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`;
  } else {
    // If it's the largest breakpoint, just check min-width
    query = `(min-width: ${minWidth}px)`;
  }

  return useMediaQuery(query);
};

/**
 * Hook for checking if screen is between two breakpoints
 * @param minBreakpoint - Minimum breakpoint key
 * @param maxBreakpoint - Maximum breakpoint key
 * @returns boolean indicating if screen is between the specified breakpoints
 */
export const useMediaQueryBetween = (
  minBreakpoint: BreakpointKey,
  maxBreakpoint: BreakpointKey
): boolean => {
  const minWidth = BREAKPOINTS[minBreakpoint];
  const maxWidth = BREAKPOINTS[maxBreakpoint] - 1;
  const query = `(min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`;
  return useMediaQuery(query);
};

/**
 * Hook that returns the current breakpoint
 * @returns Current breakpoint key or null if no breakpoint matches
 */
export const useCurrentBreakpoint = (): BreakpointKey | null => {
  const isSmall = useMediaQueryOnly('small');
  const isMedium = useMediaQueryOnly('medium');
  const isLarge = useMediaQueryOnly('large');
  const isXLarge = useMediaQueryOnly('xlarge');
  const isXXLarge = useMediaQueryOnly('xxlarge');

  if (isXXLarge) return 'xxlarge';
  if (isXLarge) return 'xlarge';
  if (isLarge) return 'large';
  if (isMedium) return 'medium';
  if (isSmall) return 'small';

  // If none match, return the smallest for screens smaller than 360px
  return 'small';
};

/**
 * Hook that returns responsive values based on current breakpoint
 * @param values - Object with breakpoint keys and corresponding values
 * @param defaultValue - Default value if no breakpoint matches
 * @returns Value corresponding to current breakpoint
 */
export const useResponsiveValue = <T,>(
  values: Partial<Record<BreakpointKey, T>>,
  defaultValue: T
): T => {
  const currentBreakpoint = useCurrentBreakpoint();

  if (!currentBreakpoint) return defaultValue;

  // Check current breakpoint first
  if (values[currentBreakpoint] !== undefined) {
    return values[currentBreakpoint] as T;
  }

  // If current breakpoint doesn't have a value, find the closest smaller breakpoint
  const breakpointKeys = Object.keys(BREAKPOINTS) as BreakpointKey[];
  const currentIndex = breakpointKeys.indexOf(currentBreakpoint);

  for (let i = currentIndex - 1; i >= 0; i--) {
    const breakpoint = breakpointKeys[i];
    if (values[breakpoint] !== undefined) {
      return values[breakpoint] as T;
    }
  }

  return defaultValue;
};

/**
 * Utility functions for common responsive checks
 */
export const useResponsive = () => {
  const isMobile = useMediaQueryDown('medium'); // < 600px
  const isTablet = useMediaQueryDown('large'); // 600px - 1023px
  const isDesktop = useMediaQueryUp('large'); // >= 1024px
  const isLargeDesktop = useMediaQueryUp('xlarge'); // >= 1270px
  const isXLargeDesktop = useMediaQueryUp('xxlarge'); // >= 1440px

  return {
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    isXLargeDesktop,
    currentBreakpoint: useCurrentBreakpoint()
  };
};

export default useMediaQuery;
